import Card from "components/card";
import StatusBadge from "components/ui/StatusBadge";
import { useLanguage } from "context/LanguageContext";
import { RevenueTransaction, formatVnd } from "../variables/revenueMock";

type Props = {
  rows: RevenueTransaction[];
};

const STATUS_TONE: Record<
  RevenueTransaction["status"],
  "success" | "warning" | "danger"
> = {
  PAID: "success",
  PENDING: "warning",
  REFUNDED: "danger",
};

const STATUS_LABEL: Record<RevenueTransaction["status"], string> = {
  PAID: "revenue.statusPaid",
  PENDING: "revenue.statusPending",
  REFUNDED: "revenue.statusRefunded",
};

const METHOD_LABEL: Record<RevenueTransaction["paymentMethod"], string> = {
  CASH: "revenue.methodCash",
  BANK_TRANSFER: "revenue.methodBank",
  CARD: "revenue.methodCard",
  MOMO: "revenue.methodMomo",
};

const TransactionsTable = ({ rows }: Props) => {
  const { t } = useLanguage();

  return (
    <Card extra="w-full h-full px-6 pb-6 pt-4">
      <div className="mb-4 flex items-center justify-between">
        <h2 className="text-lg font-bold text-ink">
          {t("revenue.recentTransactions")}
        </h2>
        <span className="text-xs font-medium text-ink-muted">
          {t("revenue.demoDataNotice")}
        </span>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full min-w-[860px] table-auto">
          <thead>
            <tr className="border-b border-line">
              <th className="py-3 pr-4 text-left text-xs font-semibold uppercase tracking-wide text-ink-muted">
                {t("revenue.txId")}
              </th>
              <th className="py-3 pr-4 text-left text-xs font-semibold uppercase tracking-wide text-ink-muted">
                {t("revenue.customer")}
              </th>
              <th className="py-3 pr-4 text-left text-xs font-semibold uppercase tracking-wide text-ink-muted">
                {t("revenue.class")}
              </th>
              <th className="py-3 pr-4 text-left text-xs font-semibold uppercase tracking-wide text-ink-muted">
                {t("revenue.amount")}
              </th>
              <th className="py-3 pr-4 text-left text-xs font-semibold uppercase tracking-wide text-ink-muted">
                {t("revenue.method")}
              </th>
              <th className="py-3 pr-4 text-left text-xs font-semibold uppercase tracking-wide text-ink-muted">
                {t("revenue.status")}
              </th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr
                key={row.id}
                className="border-b border-line last:border-b-0 hover:bg-gray-50/60 dark:hover:bg-white/5"
              >
                <td className="py-3 pr-4 text-sm font-medium text-ink">
                  {row.id}
                  <div className="text-xs font-normal text-ink-muted">
                    {row.date}
                  </div>
                </td>
                <td className="py-3 pr-4 text-sm text-ink">
                  {row.customer}
                </td>
                <td className="py-3 pr-4 text-sm text-gray-600 dark:text-gray-300">
                  {row.className}
                </td>
                <td className="py-3 pr-4 text-sm font-semibold text-ink">
                  {formatVnd(row.amount)}
                </td>
                <td className="py-3 pr-4 text-sm text-gray-600 dark:text-gray-300">
                  {t(METHOD_LABEL[row.paymentMethod])}
                </td>
                <td className="py-3 pr-4">
                  <StatusBadge
                    tone={STATUS_TONE[row.status]}
                    label={t(STATUS_LABEL[row.status])}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  );
};

export default TransactionsTable;
